/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {ChangeDetectionStrategy, Component, DestroyRef, Inject, Input, OnInit, inject, signal} from '@angular/core';
import * as _ from 'lodash';
import template from './product-list-page.component.html';

import {SignalAdminListColumn, SignalAdminResourceListStateFactory} from '../../common/controller/signal-admin-resource-list-state';
import {Uuid} from '../../../components/util/uuid';
import {LineItemTemplateApiService} from '../../line-item/line-item-template-api.service';
import {NetworkDataApiService} from '../network/network-data-api.service';
import {ProductApiService} from './product-api.service';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {UIRouterModule} from '@uirouter/angular';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import {AppSharedModule} from '../../common/app-shared.module';
import {AdminResourcePagesModule} from '../../common/controller/admin-resource-pages.module';
import {AdminListModule} from '../../common/directives/admin-list.module';
import {FormControlsModule} from '../../common/directives/form-controls/form-controls.module';
import {LayoutComponentSummaryComponent} from '../../common/layout-component-summary-component';
import {TargetingModule} from '../../targeting/targeting.module';

@Component({
  standalone: true,
  imports: [
    AppSharedModule,
    FormsModule,
    TranslateModule,
    UIRouterModule,
    ButtonsModule,
    AdminListModule,
    AdminResourcePagesModule,
    FormControlsModule,
    LayoutComponentSummaryComponent,
    TargetingModule
  ],
  selector: 'adn-product-list-page',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductListPageComponent implements OnInit {
  @Input() readOnly = false;

  readonly itemStateName = 'app.adm.products.product';

  readonly columns: SignalAdminListColumn[] = [
    {param: 'name'},
    {param: 'description'},
    {param: '', label: 'allowedDimensions', sortable: false},
    {param: 'layouts', label: 'layouts', sortable: false}
  ];

  private readonly productInfoState = signal<{[id: string]: any}>({});
  private readonly productSelectionsState = signal<any>({data: {filter: []}});
  private readonly targetingSelectionsState = signal<any>({data: {filter: []}});
  private readonly lineItemTemplatesState = signal<any[]>([]);
  private readonly isSetUpState = signal(false);
  private readonly isTargetingSetUpState = signal(false);
  private readonly productSelectionPassState = signal(false);
  private readonly productSelectionErrorState = signal(false);
  private readonly targetingSelectionPassState = signal(false);
  private readonly targetingSelectionErrorState = signal(false);

  get productInfo(): {[id: string]: any} { return this.productInfoState(); }
  set productInfo(value: {[id: string]: any}) { this.productInfoState.set(value); }
  get productSelections(): any { return this.productSelectionsState(); }
  set productSelections(value: any) { this.productSelectionsState.set(value); }
  get targetingSelections(): any { return this.targetingSelectionsState(); }
  set targetingSelections(value: any) { this.targetingSelectionsState.set(value); }
  get lineItemTemplates(): any[] { return this.lineItemTemplatesState(); }
  set lineItemTemplates(value: any[]) { this.lineItemTemplatesState.set(value); }
  get isSetUp(): boolean { return this.isSetUpState(); }
  set isSetUp(value: boolean) { this.isSetUpState.set(value); }
  get isTargetingSetUp(): boolean { return this.isTargetingSetUpState(); }
  set isTargetingSetUp(value: boolean) { this.isTargetingSetUpState.set(value); }
  get productSelectionPass(): boolean { return this.productSelectionPassState(); }
  set productSelectionPass(value: boolean) { this.productSelectionPassState.set(value); }
  get productSelectionError(): boolean { return this.productSelectionErrorState(); }
  set productSelectionError(value: boolean) { this.productSelectionErrorState.set(value); }
  get targetingSelectionPass(): boolean { return this.targetingSelectionPassState(); }
  set targetingSelectionPass(value: boolean) { this.targetingSelectionPassState.set(value); }
  get targetingSelectionError(): boolean { return this.targetingSelectionErrorState(); }
  set targetingSelectionError(value: boolean) { this.targetingSelectionErrorState.set(value); }
  editsMade = false;
  editsTargetingMade = false;
  private productQuery: Promise<void>;
  private previousProductSelections: any;
  private previousTargetingSelections: any;
  readonly list = inject(SignalAdminResourceListStateFactory).create(
    inject(ProductApiService),
    {listId: 'fullProductList', baseTable: 'product'},
    inject(DestroyRef)
  );

  constructor(
    private readonly productApi: ProductApiService,
    private readonly networkDataApi: NetworkDataApiService,
    @Inject(LineItemTemplateApiService) private LineItemTemplate: LineItemTemplateApiService
  ) {}

  get productInfoArray(): any[] {
    return Object.values(this.productInfo);
  }

  ngOnInit(): void {
    this.list.readOnly.set(this.readOnly);
    this.list.load();

    this.productQuery = firstValueFrom(this.productApi.listOptions({minimal: true, pageSize: 101})).then((page: any) => {
      const productInfo: {[id: string]: any} = {};
      _.forEach(page.results, (item: any) => {
        productInfo[item.id] = {id: item.id, name: item.name};
      });
      this.productInfo = productInfo;
    });

    this.networkDataApi.listOptions({filterByValue: 'productSelection', filterBy: 'type'}).subscribe((page: any) => {
      if (page.results.length) {
        const entry = page.results[0];
        try {
          this.productSelections = _.cloneDeep(entry);
          this.cleanData('productSelections').then(() => {
            this.previousProductSelections = _.cloneDeep(this.productSelections);
          });
        } catch (e) {
          // ignore
        }
      }
      this.isSetUp = true;
    });

    firstValueFrom(this.LineItemTemplate.listOptions()).then((data: any) => {
      this.lineItemTemplates = data.results;
    });

    this.networkDataApi.listOptions({filterByValue: 'targetingSelection', filterBy: 'type'}).subscribe((page: any) => {
      if (page.results.length) {
        const entry = page.results[0];
        try {
          this.targetingSelections = _.cloneDeep(entry);
          this.cleanData('targetingSelections').then(() => {
            this.previousTargetingSelections = _.cloneDeep(this.targetingSelections);
          });
        } catch (e) {
          // ignore
        }
      }
      this.isTargetingSetUp = true;
    });
  }

  private cleanData(param: string): Promise<void> {
    return Promise.resolve(this.productQuery).then(() => {
      const holder = this[param];
      const isTargetingSelections = param === 'targetingSelections';
      holder.data = holder.data || {};
      holder.data.filter = this.normalizeFilter(holder.data.filter);

      _.forEach(_.get(holder, ['data', 'filter']) || [], (ps: any) => {
        delete ps.editable;
        delete ps.deleteMe;
        ps.key = _.isString(ps.key) ? ps.key : '';
        ps.products = this.normalizeStringArray(ps.products);
        ps.options = this.normalizeOptions(ps.options);

        if (ps.products) {
          ps.products = _.filter(ps.products, (productId: string) => !!this.productInfo[productId]);
        }

        _.forEach(ps.options, (opt: any) => {
          delete opt.deleteMe;
          if (_.trim(ps.key).length < 1) {
            ps.deleteMe = true;
          }

          if (isTargetingSelections) {
            const hasTitle = _.trim(opt.value).length > 0;
            const hasDescription = _.trim(opt.description).length > 0;
            const hasLineItemTemplate = !!_.get(opt, ['lineItemTemplate']);
            if (!hasTitle && !hasDescription && !hasLineItemTemplate) {
              opt.deleteMe = true;
            }
          } else if (!(_.trim(opt.value).length > 0 && _.get(opt, ['products'], []).length > 0)) {
            opt.deleteMe = true;
          }

          if (opt.products) {
            opt.products = _.filter(opt.products, (productId: string) => !!this.productInfo[productId]);
          }
        });
      });

      holder.data.filter = _.filter(_.get(holder, ['data', 'filter']) || [], (ps: any) => {
        if (!ps.deleteMe) {
          ps.options = _.filter(ps.options, (opt: any) => !opt.deleteMe);
        }
        return !ps.deleteMe;
      });
    });
  }

  private normalizeFilter(rawFilter: any): any[] {
    const parsedFilter = this.parseJsonIfString(rawFilter);
    return Array.isArray(parsedFilter) ? parsedFilter : [];
  }

  private normalizeOptions(rawOptions: any): any[] {
    const parsedOptions = this.parseJsonIfString(rawOptions);
    return Array.isArray(parsedOptions) ? parsedOptions : [];
  }

  private normalizeStringArray(rawValues: any): string[] {
    const parsedValues = this.parseJsonIfString(rawValues);
    return Array.isArray(parsedValues) ? parsedValues.filter((value) => _.isString(value)) : [];
  }

  private parseJsonIfString(value: any): any {
    if (!_.isString(value)) {
      return value;
    }

    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  isProductInList(products: string[], id: string): boolean {
    return (products || []).indexOf(id) > -1;
  }

  toggleProductInList(products: string[], id: string): void {
    const idx = (products || []).indexOf(id);
    if (idx > -1) {
      products.splice(idx, 1);
    } else {
      products.push(id);
    }
    this.editsMade = true;
  }

  // Product selections methods
  setUpOptions(): void {
    this.editsMade = true;
    const emptyEntry = {key: '', editable: true, options: [{value: '', products: []}]};
    if (!this.productSelections.id) {
      this.productSelections = {
        id: Uuid.generate(),
        name: 'Product Selection',
        description: 'Product selection options',
        type: 'productSelection',
        data: {filter: [emptyEntry]}
      };
      return;
    }
    this.productSelections.data.filter.push(emptyEntry);
  }

  deleteOption(index: number): void {
    this.editsMade = true;
    _.pullAt(this.productSelections.data.filter, index);
  }

  addValue(parentIndex: number): void {
    this.editsMade = true;
    this.productSelections.data.filter[parentIndex].options.push({value: '', products: []});
  }

  removeValue(parentIndex: number, optionIndex: number): void {
    this.editsMade = true;
    _.pullAt(this.productSelections.data.filter[parentIndex].options, optionIndex);
  }

  edit(): void {
    this.editsMade = true;
  }

  saveProductSelections(): void {
    this.editsMade = false;
    this.productSelectionPass = false;
    this.productSelectionError = false;
    this.cleanData('productSelections').then(() => {
      firstValueFrom(this.networkDataApi.save(this.productSelections)).then(() => {
        this.productSelectionPass = true;
        this.previousProductSelections = _.cloneDeep(this.productSelections);
      }).catch(() => {
        this.productSelectionError = true;
      });
    });
  }

  cancelProductSelections(): void {
    this.productSelections = _.cloneDeep(this.previousProductSelections);
    this.editsMade = false;
    this.productSelectionPass = false;
    this.productSelectionError = false;
  }

  // Targeting selections methods
  setUpTargetingOptions(): void {
    this.editsTargetingMade = true;
    const emptyEntry = {key: '', products: [], editable: true, options: [this.createEmptyTargetingOption()]};
    if (!this.targetingSelections.id) {
      this.targetingSelections = {
        id: Uuid.generate(),
        name: 'Targeting Selection',
        description: 'Targeting selection options',
        type: 'targetingSelection',
        data: {filter: [emptyEntry]}
      };
      return;
    }
    this.targetingSelections.data.filter.push(emptyEntry);
  }

  deleteTargetingOption(index: number): void {
    this.editsTargetingMade = true;
    _.pullAt(this.targetingSelections.data.filter, index);
  }

  openTargetingEditor(ps: any): void {
    ps.options = this.normalizeOptions(ps.options);
    if (!ps.options.length) {
      ps.options.push(this.createEmptyTargetingOption());
    }
    ps.editable = true;
  }

  addTargetingValue(parentIndex: number): void {
    this.editsTargetingMade = true;
    this.targetingSelections.data.filter[parentIndex].options.push(this.createEmptyTargetingOption());
  }

  removeTargetingValue(parentIndex: number, optionIndex: number): void {
    this.editsTargetingMade = true;
    _.pullAt(this.targetingSelections.data.filter[parentIndex].options, optionIndex);
  }

  editTargeting(): void {
    this.editsTargetingMade = true;
  }

  saveTargetingSelections(): void {
    this.editsTargetingMade = false;
    this.targetingSelectionPass = false;
    this.targetingSelectionError = false;
    this.cleanData('targetingSelections').then(() => {
      firstValueFrom(this.networkDataApi.save(this.targetingSelections)).then(() => {
        this.targetingSelectionPass = true;
        this.previousTargetingSelections = _.cloneDeep(this.targetingSelections);
      }).catch(() => {
        this.targetingSelectionError = true;
      });
    });
  }

  cancelTargetingSelections(): void {
    this.targetingSelections = _.cloneDeep(this.previousTargetingSelections);
    this.editsTargetingMade = false;
    this.targetingSelectionPass = false;
    this.targetingSelectionError = false;
  }

  private createEmptyTargetingOption(): any {
    return {value: '', description: '', lineItemTemplate: null};
  }
}
